import { useAuth } from '../context/AuthContext'
import { config } from '../config/environment'
import { Lock, Server, Shield } from 'lucide-react'

export default function LoginPage() {
  const { login, isLoading, error } = useAuth()

  const handleLogin = async () => {
    try {
      await login()
    } catch (err) {
      console.error('Login error:', err)
    }
  }

  const envLabel = config.environment === 'production' ? null : config.environment.toUpperCase()
  const envClass = config.environment === 'beta'
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-orange-50 text-orange-800 border-orange-200'

  return (
    <div className="min-h-screen w-full bg-white text-slate-900 flex">
      {/* Brand pane — desktop only */}
      <aside className="hidden lg:flex relative w-1/2 flex-col justify-between p-12 overflow-hidden text-white" style={{ backgroundColor: '#F97316' }}>

        {/* Top: logo + wordmark */}
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
            <img src="/aitryx.svg" alt="" className="w-7 h-7" />
          </div>
          <div className="text-[24px] font-semibold tracking-tight leading-none">
            aitryx <span className="text-white/60 font-normal">partner</span>
          </div>
        </div>

        {/* Middle: pitch */}
        <div className="relative max-w-md">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60 mb-3">
            Partner portal
          </div>
          <h1 className="text-[34px] leading-[1.15] font-semibold tracking-tight">
            Your customers, tickets and approvals — in one workspace.
          </h1>
          <p className="text-[14px] text-white/75 mt-4 leading-relaxed max-w-sm">
            Sign in to triage tickets, run recurring tasks, and provision marketplace orders across every customer you manage.
          </p>

          <ul className="mt-8 space-y-2.5">
            {[
              'Unified ticket queue and approval gates across customers',
              'Recurring task automation with SLA tracking',
              'One-click marketplace ordering and provisioning',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[13px] text-white/85 leading-relaxed">
                <span className="mt-[7px] w-1 h-1 rounded-full bg-white/60 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom: subtle attribution */}
        <div className="relative text-[11px] text-white/55 tracking-wide">
          aitryx · all systems nominal
        </div>
      </aside>

      {/* Sign-in pane */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-12 relative bg-slate-50/40">
        {envLabel && (
          <div className="absolute top-6 right-6">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider border ${envClass}`}>
              {envLabel}
            </span>
          </div>
        )}

        {/* Mobile-only logo strip */}
        <div className="lg:hidden mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white shadow-sm border border-slate-200 flex items-center justify-center">
            <img src="/aitryx.svg" alt="" className="w-6 h-6" />
          </div>
          <div className="text-[20px] font-semibold tracking-tight text-slate-900">
            aitryx <span className="text-slate-400 font-normal">partner</span>
          </div>
        </div>

        <div className="w-full max-w-[400px]">
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 ring-1 ring-slate-200/70 px-8 sm:px-10 py-9">
            <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">Sign in</h2>
            <p className="text-[13px] text-slate-500 mt-1">
              Use your Microsoft work account to access the partner portal.
            </p>

            {error && (
              <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                <p className="text-[12.5px] text-rose-700 leading-snug">{error}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handleLogin}
              disabled={isLoading}
              className="mt-6 w-full h-11 inline-flex items-center justify-center gap-3 rounded-lg bg-slate-900 text-white text-[13.5px] font-semibold shadow-sm hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="w-4 h-4 rounded-full border-2 border-slate-500 border-t-white animate-spin" />
              ) : (
                <svg className="w-[18px] h-[18px]" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect width="9.5" height="9.5" fill="#F25022" />
                  <rect x="11" width="9.5" height="9.5" fill="#7FBA00" />
                  <rect y="11" width="9.5" height="9.5" fill="#00A4EF" />
                  <rect x="11" y="11" width="9.5" height="9.5" fill="#FFB900" />
                </svg>
              )}
              {isLoading ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
                  Secured by Microsoft Entra ID
                </span>
              </div>
            </div>

            <div className="flex justify-center gap-6 text-[11px] text-slate-400">
              <div className="inline-flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                <span>TLS encrypted</span>
              </div>
              <div className="inline-flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                <span>SSO</span>
              </div>
              <div className="inline-flex items-center gap-1.5">
                <Server className="h-3.5 w-3.5" />
                <span>Azure AD</span>
              </div>
            </div>
          </div>

          <p className="text-center text-[11.5px] text-slate-400 mt-6">
            &copy; {new Date().getFullYear()} Aitryx. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  )
}
